import { useCallback, useMemo, useState } from "react";
import { MAX_COMPETENCY_COUNT, type UseCompanyAdminResult } from "./use-company-admin";

// The company's expertise as a DRAFT, the counterpart of use-company-member-draft: entries are added
// and removed on screen and nothing reaches genesis until the SaveBar saves the scope.
//
// THREE INTENTIONS, AND ONE OF THEM BECOMES TWO REQUESTS. A listed entry can be removed, renamed or
// moved to another level; the last two are one intention here (`stageEdit`) because that is what the
// admin means, and two requests in apply() because that is what genesis needs — `setCompanyCompetency`
// ADDS rather than upserts, refusing a label the company already lists, so altering a stored row is a
// remove followed by a fresh add. The label is the identity throughout (genesis holds it unique per
// company, folding case and surrounding whitespace, so this folds them the same way), which is why
// every stager takes the ORIGINAL label: a row can be renamed while it is staged.
//
// THE EVIDENCE FOR "ADDS RATHER THAN UPSERTS", because an earlier revision of this comment asserted
// the opposite and `stageAdd` was written to match the wrong model: genesis ends the mutation in
// `onConflict(company_id, folded(label)).doNothing()` and refuses a second row for the same label with
// GENESIS001247 (CompanyCompetencyController). Remove-then-add is therefore not a workaround but the
// only route @adr SPARK-0004-company-capabilities@1 gives — "Eine Stufe zu ändern heißt entfernen und neu hinzufügen" — and the one
// the refusal copy itself prescribes.
//
// STAGE-EDIT IS NOT THE ONLY WAY TO RE-SCORE, which is why `stageAdd` carries its own branch for it.
// A row staged for REMOVAL leaves `projected`'s live set, so its chip is gone from the ladder and
// `stageEdit` can no longer reach it — but the "+" on a rung can still add that same label back, and
// `hasLabel` deliberately ignores removed rows so it does not refuse. Adding it back at a DIFFERENT
// level is a re-score and both halves have to stay staged; see the note in `stageAdd`.
//
// THE ORDER IS THE OPPOSITE OF THE MEMBER DRAFT'S, and for the mirror-image reason. There, adds go
// first because genesis refuses a write that would leave no admin. Here, REMOVALS go first because
// genesis refuses a write that would exceed MAX_COMPETENCY_COUNT: an admin at the cap who stages
// "drop this one, add that one" means both together, and applied the other way round the add is
// refused, correctly and uselessly.

export interface StoredCompetency {
  label: string;
  degree: number;
}

export interface ProjectedCompetency {
  label: string;
  degree: number;
  /** True for an entry that is not stored yet. */
  isNew: boolean;
  /** True for a stored entry staged for removal — still listed, said so in words. */
  isRemoved: boolean;
  /** The label changed, the level changed, or both — one staged operation either way. */
  isEdited: boolean;
  /**
   * THE STABLE HANDLE, and why every stager below takes it rather than the visible label. A row can
   * be renamed while it is staged, so the label a control was rendered with is not the one the next
   * click would name. `original` is what the company actually stores — or, for a staged addition,
   * what it was first called — and it does not move.
   */
  original: string;
}

export interface CompanyCompetencyDraft {
  /** The list as it will be once saved. */
  projected: ProjectedCompetency[];
  /** True while anything is staged — what the SaveBar reads. */
  dirty: boolean;
  /** True when the projected list is already at MAX_COMPETENCY_COUNT. */
  listFull: boolean;
  /** True when the projected list already carries this label, folded the way genesis folds it. */
  hasLabel: (label: string) => boolean;
  stageAdd: (label: string, degree: number) => void;
  /**
   * Changes an entry's label, its level, or both.
   *
   * genesis has no re-score: `setCompanyCompetency` refuses a label the company already lists
   * (GENESIS001247), so changing anything about a stored row is a remove followed by a fresh add.
   * That is one intention of the admin's and it stays one staged operation here; {@link apply} is
   * where it becomes two requests, in the order genesis needs them.
   *
   * A patch that leaves the row equal to what is stored clears the staging rather than recording a
   * no-op — opening the editor and closing it again must not queue a remove and an add.
   */
  stageEdit: (original: string, patch: { label?: string; degree?: number }) => void;
  stageRemove: (label: string) => void;
  /** Takes an entry back to its stored state — and drops a staged addition entirely. */
  unstage: (label: string) => void;
  discard: () => void;
  /**
   * Applies every staged intent: removals, then edits, then additions.
   *
   * Resolves with the messages of whatever genesis refused; those intents stay in the draft so they
   * can be corrected and saved again. An empty array means everything landed.
   *
   * It took an `alsoAdd` argument for the case where a typed entry was added and saved in the same
   * press, since `stageAdd` is a state update and the apply of that render still closed over the
   * draft as it was. There is no such press any more — the ladder's own panel stages on confirm and
   * saving is a separate act — so the parameter went with the form that needed it.
   */
  apply: () => Promise<string[]>;
}

interface DraftState {
  added: StoredCompetency[];
  removed: string[];
  /** fold(original label) -> what it should become. `from` keeps the untouched text for the remove. */
  edited: Record<string, { from: string; to: StoredCompetency }>;
}

const EMPTY: DraftState = { added: [], removed: [], edited: {} };

/**
 * genesis folds case and surrounding whitespace when it decides two labels are the same one.
 *
 * `toLowerCase`, NOT `toLocaleLowerCase`: the latter follows the browser's locale, and in a Turkish
 * one "I" folds to "ı" rather than "i", so the same two labels would be a duplicate for one admin
 * and not for another. This fold only decides what the form OFFERS — genesis re-checks it and its
 * refusal is still shown — so being slightly narrower than SQL `lower()` costs a refusal the user
 * sees, while being locale-dependent would cost them a rule that changes under their feet.
 */
const fold = (label: string) => label.trim().toLowerCase();

export function useCompanyCompetencyDraft(
  competencies: readonly StoredCompetency[],
  admin: UseCompanyAdminResult,
): CompanyCompetencyDraft {
  const [draft, setDraft] = useState<DraftState>(EMPTY);

  const projected = useMemo<ProjectedCompetency[]>(() => {
    const removed = new Set(draft.removed.map(fold));

    return [
      ...competencies.map((competency) => {
        const edit = draft.edited[fold(competency.label)];
        return {
          // What the row WILL say once saved, so the ladder puts the chip on the rung the admin
          // dropped it on rather than on the one genesis still holds.
          label: edit?.to.label ?? competency.label,
          degree: edit?.to.degree ?? competency.degree,
          isNew: false,
          isRemoved: removed.has(fold(competency.label)),
          isEdited: edit !== undefined,
          original: competency.label,
        };
      }),
      // Appended, like a staged member: a new entry has no place in genesis's ordering yet, and the
      // stored rows stay where the admin last saw them.
      ...draft.added.map((competency) => ({
        label: competency.label,
        degree: competency.degree,
        isNew: true,
        isRemoved: false,
        isEdited: false,
        // A staged addition is edited in place, so it needs no separate handle — but every row has
        // to answer to the same one, and for an addition the first label is as stable as anything.
        original: competency.label,
      })),
    ];
  }, [competencies, draft]);

  const dirty =
    draft.added.length > 0 ||
    draft.removed.length > 0 ||
    Object.keys(draft.edited).length > 0;

  // Over the PROJECTION: an entry staged for removal has already freed its slot as far as the admin
  // is concerned, and one staged for adding has taken one.
  const count = projected.filter((entry) => !entry.isRemoved).length;

  const hasLabel = useCallback(
    (label: string) =>
      projected.some((entry) => !entry.isRemoved && fold(entry.label) === fold(label)),
    [projected],
  );

  const stageAdd = useCallback(
    (label: string, degree: number) => {
      const trimmed = label.trim();
      const stored = competencies.find((entry) => fold(entry.label) === fold(trimmed));

      setDraft((previous) => {
        if (previous.removed.some((removed) => fold(removed) === fold(trimmed))) {
          // ADDING BACK AT THE SAME LEVEL is the admin changing their mind: the removal goes and no
          // addition is needed, because the entry is still there with exactly that level. (Undo on
          // the row does the same thing more directly.)
          if (stored && stored.degree === degree) {
            return { ...previous, removed: previous.removed.filter((r) => fold(r) !== fold(trimmed)) };
          }

          // ADDING BACK AT A DIFFERENT LEVEL is a RE-SCORE, and it is the only way the concept gives
          // to perform one (@adr SPARK-0004-company-capabilities@1). Both halves have to be staged: the removal STAYS and the new
          // level is added beside it, so `apply`'s removals-then-additions ordering performs exactly
          // the sequence genesis requires. Cancelling the removal here — which is what this branch
          // used to do unconditionally — silently threw the chosen level away, left the row at its
          // stored one, and cleared the dirty flag so nothing on screen said a thing.
          return {
            ...previous,
            added: previous.added.some((entry) => fold(entry.label) === fold(trimmed))
              ? previous.added
              : [...previous.added, { label: trimmed, degree }],
          };
        }
        if (previous.added.some((entry) => fold(entry.label) === fold(trimmed))) return previous;

        return { ...previous, added: [...previous.added, { label: trimmed, degree }] };
      });
    },
    [competencies],
  );

  const stageEdit = useCallback(
    (original: string, patch: { label?: string; degree?: number }) => {
      setDraft((previous) => {
        const key = fold(original);
        // A row that only exists in the draft is edited in place. Nothing is stored for it, so there
        // is nothing to remove-and-re-add and no reason to record an intent about it.
        const staged = previous.added.find((entry) => fold(entry.label) === key);
        if (staged) {
          const next = {
            label: patch.label ?? staged.label,
            degree: patch.degree ?? staged.degree,
          };
          return {
            ...previous,
            added: previous.added.map((entry) => (entry === staged ? next : entry)),
          };
        }

        const stored = competencies.find((entry) => fold(entry.label) === key);
        if (!stored) return previous;
        const current = previous.edited[key]?.to ?? stored;
        // NOT TRIMMED HERE, and that is the whole of a bug worth remembering: this fires on every
        // keystroke, so trimming meant the space in "Regulatory Affairs" was eaten the instant it
        // was typed and the next letter landed against the previous word. What is stored is what was
        // typed; `fold` trims for every comparison, setCompetency trims before it writes, and the
        // save gate trims before it refuses.
        const next = {
          label: patch.label ?? current.label,
          degree: patch.degree ?? current.degree,
        };

        // Back to what genesis holds: drop the intent rather than record a remove-and-re-add that
        // would change nothing. Opening the editor and closing it again must cost no requests.
        const edited = { ...previous.edited };
        if (fold(next.label) === fold(stored.label) && next.degree === stored.degree) {
          delete edited[key];
        } else {
          edited[key] = { from: stored.label, to: next };
        }
        return { ...previous, edited };
      });
    },
    [competencies],
  );

  const stageRemove = useCallback((label: string) => {
    setDraft((previous) => {
      // Removing something that was only staged for adding drops the intent rather than recording a
      // removal genesis would refuse — there is nothing there to remove.
      if (previous.added.some((entry) => fold(entry.label) === fold(label))) {
        return {
          ...previous,
          added: previous.added.filter((entry) => fold(entry.label) !== fold(label)),
        };
      }
      // REMOVING SUPERSEDES EDITING, and dropping the edit is not tidiness. apply() skips an edit
      // whose row it has just removed — rightly, there is nothing left to rewrite — but an intent
      // that is never applied is also never cleared, so the draft would stay dirty for good and the
      // SaveBar would never go quiet after a perfectly successful save.
      const edited = { ...previous.edited };
      delete edited[fold(label)];
      return {
        ...previous,
        edited,
        removed: previous.removed.some((r) => fold(r) === fold(label))
          ? previous.removed
          : [...previous.removed, label],
      };
    });
  }, []);

  const unstage = useCallback((label: string) => {
    setDraft((previous) => {
      const edited = { ...previous.edited };
      delete edited[fold(label)];
      return {
        added: previous.added.filter((entry) => fold(entry.label) !== fold(label)),
        removed: previous.removed.filter((removed) => fold(removed) !== fold(label)),
        edited,
      };
    });
  }, []);

  const discard = useCallback(() => setDraft(EMPTY), []);

  const apply = useCallback(async (): Promise<string[]> => {
    const failures: string[] = [];
    const appliedRemovals = new Set<string>();
    const appliedAdds = new Set<string>();
    const appliedEdits = new Set<string>();
    // Edits whose remove landed but whose add did not — see the note below.
    const orphaned: StoredCompetency[] = [];

    for (const label of draft.removed) {
      const result = await admin.removeCompetency(label);
      if (result.ok) appliedRemovals.add(fold(label));
      else failures.push(result.message);
    }

    // EDITS ARE A REMOVE AND AN ADD, in that order and per entry, because genesis has no re-score.
    // Per entry rather than "all removes, then all adds": if the add half is refused, only that one
    // row is left in the odd state of having lost its stored value, and it is still staged so the
    // admin can see it and try again. Batching would strip several rows before writing any of them.
    //
    // The remove happens even when only the LEVEL changed, and skipping it would be the bug: genesis
    // refuses an add whose label the company already lists, so the row has to be gone before the new
    // level can be written.
    for (const [key, edit] of Object.entries(draft.edited)) {
      if (appliedRemovals.has(key)) {
        // ALREADY REMOVED BY THIS SAVE, so there is nothing left to rewrite — but the intent still has
        // to be marked applied or it is never cleared below, and the draft stays dirty for good with
        // the SaveBar lit over work that has already happened. `stageRemove` drops the edit to stop
        // this, and that guard only covers ONE order: staging the removal FIRST and then editing the
        // same stored label re-creates the intent behind it, which is how this path is reached.
        appliedEdits.add(key);
        continue;
      }
      const removal = await admin.removeCompetency(edit.from);
      if (!removal.ok) {
        failures.push(removal.message);
        continue;
      }
      const addition = await admin.setCompetency(edit.to.label, edit.to.degree);
      if (addition.ok) {
        appliedEdits.add(key);
        continue;
      }
      // THE HALF-APPLIED EDIT, and the one state this draft must not leave behind. The remove landed
      // and the add did not, so the stored row is gone: the refetch drops it from `competencies`, and
      // an intent that is projected THROUGH a stored row would have nothing left to hang on. It would
      // sit in the draft dirty, invisible and unretriable — the SaveBar lit over work nobody can see.
      //
      // What is outstanding is no longer an edit but an ADD, so it becomes one. The row reappears as
      // a staged addition, says it will be added when saved, and the same Save writes it.
      failures.push(addition.message);
      appliedEdits.add(key);
      orphaned.push(edit.to);
    }

    for (const entry of draft.added) {
      const result = await admin.setCompetency(entry.label, entry.degree);
      if (result.ok) appliedAdds.add(fold(entry.label));
      else failures.push(result.message);
    }

    // What succeeded leaves the draft; what was refused stays in it, so the admin can see what is
    // still outstanding and press Save again once they have made room or corrected the label.
    setDraft((previous) => ({
      added: [
        ...previous.added.filter((entry) => !appliedAdds.has(fold(entry.label))),
        ...orphaned.filter((entry) => !previous.added.some((a) => fold(a.label) === fold(entry.label))),
      ],
      removed: previous.removed.filter((label) => !appliedRemovals.has(fold(label))),
      edited: Object.fromEntries(
        Object.entries(previous.edited).filter(([key]) => !appliedEdits.has(key)),
      ),
    }));

    return failures;
  }, [admin, draft]);

  return {
    projected,
    dirty,
    listFull: count >= MAX_COMPETENCY_COUNT,
    hasLabel,
    stageAdd,
    stageEdit,
    stageRemove,
    unstage,
    discard,
    apply,
  };
}
